import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState, useMemo } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Card,
  Stack,
  Typography,
  TextField,
  FormControl,
  useMediaQuery,
  MenuItem,
  CircularProgress,
  Button,
  Tooltip, IconButton
} from '@mui/material';

import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import CustomFilter from 'src/components/filter/CustomFilter';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';

const statusInfoText = `
PET ARABASI & ÇOCUK ARABASI
• Ödeme Bekleniyor: Müşterinin ödeme yapması bekleniyor.
• Onay Bekliyor: Ödeme alındı ve talep oluştu.
• Onaylandı: Desk talebi kabul etti, araç atandı ve müşteriye verildi.
• Tamamlandı: Araç desk’e geri bırakıldı ve işlem tamamlandı.

HEDİYE PAKETİ
• Ödeme Bekleniyor: Müşterinin ödeme yapması bekleniyor.
• Onay Bekliyor: Ödeme alındı ve talep oluştu.
• Tamamlandı: Desk işlemi kabul etti. Paketleme yapılacağı için üçüncü statü yoktur.
`;

export default function EasyPointServices() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const showPhoneColumn = parsedUser?.type === 'highadmin';
  const isMobile = useMediaQuery('(max-width:768px)');

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('no');
  const [filterName, setFilterName] = useState('');

  const [originalData, setOriginalData] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [serviceType, setServiceType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showPopUpError, setShowPopUpError] = useState(false);
  const [avmName, setAvmName] = useState('');

  const [malls, setMalls] = useState([]);

  const [filterModal, setFilterModal] = useState(false);
  const handleModal = () => setFilterModal(true);
  const handleCloseModal = () => setFilterModal(false);
  const [statusFilter, setStatusFilter] = useState('');

  const today = new Date();
  const defaultEndDate = today.toISOString().split('T')[0];
  const tempStart = new Date(today);
  tempStart.setDate(tempStart.getDate() - 30);
  const defaultStartDate = tempStart.toISOString().split('T')[0];

  const formatDateTime = (ts) => {
    const d = new Date(Number(ts) * 1000);
    return (
      `${String(d.getDate()).padStart(2, '0')}.${String(
        d.getMonth() + 1
      ).padStart(2, '0')}.${d.getFullYear()} ` +
      `${String(d.getHours()).padStart(2, '0')}:${String(
        d.getMinutes()
      ).padStart(2, '0')}`
    );
  };

  const mapServiceType = (type) => {
    switch (type) {
      case 'gift':
        return 'Hediye Paketi';
      case 'pet-car':
        return 'Evcil Hayvan Aracı';
      case 'kid-car':
        return 'Çocuk Arabası';
      case 'handsfree-order':
      case 'handsfree-post':
      default:
        return 'Handsfree';
    }
  };

  const hfOrderLabels = {
    1: 'Onaylandı',
    2: 'İptal Edildi',
    3: 'Yanıt Bekleniyor',
    6: 'Müşteri Onayı Bekleniyor',
  };
  const hfPostLabels = {
    1: 'Tamamlandı',
    2: 'İptal Edildi',
    3: 'Beklemede',
    4: 'Porter’da',
    5: 'Şubede',
    6: 'Müşteri Onayı Bekleniyor',
  };
  const statusLabels = {
    1: 'Tamamlandı',
    2: 'İptal',
    99: 'Süre Aşıldı',
    3: 'Onay Bekliyor',
    4: 'Onaylandı',
    refund: 'İade Edildi',
  };

  const mapStatus = (statusCode, rawType) => {
    const code = Number(statusCode);

    if (rawType === 'gift' && code === 4) {
      return 'Tamamlandı';
    }

    if (rawType === 'handsfree-order') {
      return hfOrderLabels[code] ?? statusCode;
    }
    if (rawType === 'handsfree-post') {
      return hfPostLabels[code] ?? statusCode;
    }

    return statusLabels[code] ?? statusCode;
  };
  const getDisplayStatus = (item) => {
    if (item?.isRefund === true) {
      return statusLabels.refund;
    }

    if (isOverdue(item)) {
      return statusLabels[99];
    }

    return mapStatus(item.status, item.type);
  };

  const isOverdue = (item) => {
    if (item?.isPaid !== '1') {
      return false;
    }
    const created = Number(item?.createdDateTime);
    const duration = Number(item?.duration);
    if (!created || Number.isNaN(created) || !duration || Number.isNaN(duration)) {
      return false;
    }
    const statusCode = Number(item?.status);
    if (statusCode === 1 || statusCode === 2) {
      return false;
    }
    const nowSec = Date.now() / 1000;
    return nowSec > created + duration;
  };

  const getPriceText = (item) => {
    if (item?.isPaid === '0') {
      return 'Ödeme Bekliyor';
    }
    if (item.type === 'gift' || item.type === 'pet-car' || item.type === 'kid-car') {
      return `${item.price} ${item.currency}`;
    }
    if (item.deliveryTypeInfo) {
      const curr =
        item.deliveryTypeInfo.currency === 'TRY'
          ? 'TL'
          : item.deliveryTypeInfo.currency;
      return `${item.deliveryTypeInfo.price} ${curr}`;
    }
    return '';
  };

  const mapPaymentType = (paymentType) => {
    switch (paymentType) {
      case 'physical-pos':
        return 'Fiziksel POS';
      case 'cash':
        return 'Nakit';
      case 'online-payment':
        return 'Online Ödeme';
      case 'coupon':
        return '-';
      default:
        return paymentType || '-';
    }
  };

  const getMallIdFromTitle = (mallTitle) => {
    const mall = malls.find((item) => item.title === mallTitle);
    return mall?.id || mall?.avmID || mall?.mallID || '-';
  };

  const getPointTitleFromId = (poiID, pointList) => {
    if (!poiID) {
      return '-';
    }
    const point = (pointList || []).find(
      (item) => String(item?.id ?? item?.pointID ?? item?.poiID) === String(poiID)
    );
    return point?.title || '-';
  };

  function exportToExcel() {
    const temp = servicesList.map((it) => ({
      'Sipariş ID': it.requestID,
      AVMID: getMallIdFromTitle(it.avm),
      AVM: it.avm,
      'Nokta ID': it.pointID,
      'Nokta Adı': it.pointTitle,
      'Hizmet Türü': it.serviceType,
      Tarih: it.date,
      'Miktar/Birim': it.birim,
      İsim: it.user,
      ...(showPhoneColumn ? { 'Telefon': it.phoneNumber } : {}),
      'Teslimat Noktası': it.deliveryPoint,
      Renk: it.color,
      'Ödeme Tipi': it.paymentType,
      'Kupon Var/Yok': it.hasCoupon,
      Fiyat: it.price,
      'İade Tutarı': it.refundAmount,
      Statü: it.status,
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: temp,
        header: {
          'Sipariş ID': 'string',
          AVMID: 'string',
          AVM: 'string',
          'Nokta ID': 'string',
          'Nokta Adı': 'string',
          'Hizmet Türü': 'string',
          Tarih: 'string',
          'Miktar/Birim': 'string',
          İsim: 'string',
          ...(showPhoneColumn ? { 'Telefon': 'string' } : {}),
          'Teslimat Noktası': 'string',
          Renk: 'string',
          'Ödeme Tipi': 'string',
          'Kupon Var/Yok': 'string',
          Fiyat: 'string',
          'İade Tutarı': 'string',
          Statü: 'string',
        },
        sheetTitle: 'EasyPoint Hizmetleri',
      })
      .then((response) => {
        if (response.data.status) {
          const w = window.open(response.data.result, '_blank');
          if (!w || w.closed || typeof w.closed == 'undefined') {
            setShowPopUpError(true);
            throw new Error('Pop-up engellendi');
          }
        }
      })
      .catch((err) => {
        console.error(err);
        setShowPopUpError(true);
      });
  }

  useEffect(() => {
    setStatusFilter('');
  }, [serviceType]);

  const availableStatuses = useMemo(() => {
    if (!serviceType) {
      return [];
    }
    let labels = {};
    if (serviceType === 'handsfree') {
      labels = { ...hfOrderLabels, ...hfPostLabels };
    } else {
      labels = statusLabels;
    }
    const statuses = Object.entries(labels).map(([, value]) => ({
      value,
      label: value,
    }));

    if (serviceType !== 'handsfree') {
      statuses.push({
        value: statusLabels.refund,
        label: statusLabels.refund,
      });
    }

    return statuses.filter(
      (status, index, array) =>
        array.findIndex((item) => item.value === status.value) === index
    );
  }, [serviceType]);

  const fetchServices = async (from, to) => {
    setLoading(true);
    try {
      const [res, pointsRes] = await Promise.all([
        Services.GetEPServices(parsedUser.token, from, to),
        Services.GetPoints(parsedUser.token),
      ]);
      const list = res.data.result || [];
      const pointList = pointsRes?.data?.result || [];
      const mapped = list.map((it, idx) => {
        const userName = it.name ? `${it.name} ${it.lastname}` : '';
        let birim = '';
        if (it.type === 'handsfree-post') {
          birim = it.ordersTotalPackageCount
            ? `${it.ordersTotalPackageCount} adet`
            : '';
        } else if (it.type !== 'handsfree-order') {
          if (it.duration === "84000") {
            birim = '1 Gün';
          }
          else {
            if (it.duration) {
              birim = (Number(it.duration) / 3600) + " saat"
            }
            else {
              birim = "-"
            }
          }
        }
        return {
          no: idx + 1,
          requestID: it.requestID || '-',
          user: userName,
          rawType: it.type,
          avm: it.mallTitle,
          pointID: it.poiID || '-',
          pointTitle: getPointTitleFromId(it.poiID, pointList),
          serviceType: mapServiceType(it.type),
          date: formatDateTime(it.createdDateTime),
          birim,
          phoneNumber: it.billing?.phone,
          deliveryPoint: it.deliveryTypeInfo?.text || '',
          color: it.color || '-',
          paymentType: mapPaymentType(it.paymentType),
          hasCoupon: it.usedCoupon === true ? 'Var' : 'Yok',
          price: getPriceText(it),
          refundAmount:
            it.isRefund === true
              ? `${it.refundAmount || '-'} ${it.currency || ''}`.trim()
              : '-',
          isPaid: it.isPaid || '',
          isRefund: it.isRefund === true,
          status: getDisplayStatus(it),
        };
      });
      setOriginalData(mapped);
      setServicesList(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching services', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchMalls = async () => {
    try {
      const res = await Services.GetMalls(parsedUser.token);
      setMalls(res.data.result || []);
    } catch (err) {
      console.error('Error fetching malls', err);
    }
  };

  useEffect(() => {
    fetchServices(defaultStartDate, defaultEndDate);
    fetchMalls();
  }, []);

  const getFilteredServiceList = async () => {
    let dataSource = originalData;

    if (startDate || endDate) {
      const from = startDate || defaultStartDate;
      const to = endDate || defaultEndDate;
      dataSource = await fetchServices(from, to);
    }

    const filtered = dataSource.filter((item) => {
      const byType = serviceType
        ? serviceType === 'handsfree'
          ? ['handsfree-order', 'handsfree-post'].includes(item.rawType)
          : item.rawType === serviceType
        : true;
      const byAvm = avmName ? item.avm === avmName : true;
      const byStatus = statusFilter ? item.status === statusFilter : true;

      return byType && byAvm && byStatus;
    });

    setServicesList(filtered);
    handleCloseModal();
  };

  const clearFilters = async () => {
    setServiceType('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setAvmName('');
    handleCloseModal();
    await fetchServices(defaultStartDate, defaultEndDate);
  };

  function descendingComparator(a, b, key) {
    if (b[key] < a[key]) return -1;
    if (b[key] > a[key]) return 1;
    return 0;
  }
  function getComparator(order, key) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, key)
      : (a, b) => -descendingComparator(a, b, key);
  }
  function applySortFilter(arr, cmp, query) {
    const stabilized = arr.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const comp = cmp(a[0], b[0]);
      if (comp !== 0) return comp;
      return a[1] - b[1];
    });
    const sorted = stabilized.map((el) => el[0]);
    if (query) {
      return filter(
        sorted,
        (row) =>
          row.avm.toLowerCase().includes(query.toLowerCase()) ||
          row.serviceType.toLowerCase().includes(query.toLowerCase())
      );
    }
    return sorted;
  }

  const filteredServices = applySortFilter(
    servicesList,
    getComparator(order, orderBy),
    filterName
  );

  const TABLE_HEAD = [
    { id: 'requestID', label: 'Sipariş ID', alignRight: false },
    { id: 'avm', label: 'AVM', alignRight: false },
    { id: 'serviceType', label: 'Hizmet Türü', alignRight: false },
    { id: 'date', label: 'Tarih', alignRight: false },
    { id: 'birim', label: 'Miktar/Birim', alignRight: false },
    { id: 'user', label: 'İsim', alignRight: false },
    ...(showPhoneColumn ? [{ id: 'phoneNumber', label: 'Telefon', alignRight: false }] : []),
    { id: 'deliveryPoint', label: 'Teslimat Noktası', alignRight: false },
    { id: 'color', label: 'Renk', alignRight: false },
    { id: 'paymentType', label: 'Ödeme Tipi', alignRight: false },
    { id: 'hasCoupon', label: 'Kupon Var/Yok', alignRight: false },
    { id: 'price', label: 'Fiyat', alignRight: false },
    { id: 'refundAmount', label: 'İade Tutarı', alignRight: false },
    { id: 'status', label: 'Statü', alignRight: false },
  ];

  return (
    <>
      <Helmet>
        <title>Easy Point Hizmetleri</title>
      </Helmet>

      <Stack sx={{ px: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Easy Point Hizmetleri</Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent={isMobile ? 'space-between' : 'end'}
          mb={5}
        >
          <ExportToExcelButton
            showPopUpError={showPopUpError}
            setShowPopUpError={setShowPopUpError}
            onClick={exportToExcel}
          />
          {isMobile && <FilterViewButton onClick={handleModal} />}

        </Stack>

        <Card>
          <CustomFilter
            modalType="drawer"
            filterModal={filterModal}
            handleCloseModal={handleCloseModal}
          >
            <Stack spacing={2} p={2}>
              <FormControl fullWidth>
                <TextField
                  select
                  label="Hizmet Türü"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="handsfree">Handsfree</MenuItem>
                  <MenuItem value="gift">Hediye Paketi</MenuItem>
                  <MenuItem value="pet-car">Evcil Hayvan Aracı</MenuItem>
                  <MenuItem value="kid-car">Çocuk Arabası</MenuItem>
                </TextField>
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  select
                  label="Statü"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={!serviceType}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {availableStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <TextField
                    label="Başlangıç Tarihi"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <TextField
                    label="Bitiş Tarihi"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </FormControl>
              </Stack>

              <FormControl fullWidth>
                <TextField
                  select
                  label="AVM"
                  value={avmName}
                  onChange={(e) => setAvmName(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {malls.map((mall) => (
                    <MenuItem key={mall.title} value={mall.title}>
                      {mall.title}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <ClearButton onClick={clearFilters} />
                <FilterButton onClick={getFilteredServiceList} />
              </Stack>
            </Stack>
          </CustomFilter>

          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <DynamicTable
              data={filteredServices}
              headers={TABLE_HEAD}
              initialOrder="no"
              initalOrderDirection="asc"
              isOrdered
              isMobile={isMobile}
              handleModal={handleModal}
              handleCloseModal={handleCloseModal}
            >
              <Tooltip
                title={
                  <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {statusInfoText}
                  </pre>
                }
                placement="top"
                arrow
              >
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

            </DynamicTable>
          )}
        </Card>
      </Stack>
    </>
  );
}
